import React from 'react';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { Banner } from 'components/banner';
import { DISMISSAL_DURATIONS } from 'components/banner/constants/dismissalDurations';
import { TenantId } from '@tenants/types';
import { MAINTENANCE_BANNER_FLAG } from 'constants/feature-flags';
import { Build as MaintenanceIcon } from '@mui/icons-material';
import { formatI18nDate } from 'utils/i18n-format-date';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS } from 'types/locales';

export interface MaintenanceBannerConfig {
  date?: string; // ISO 8601 date string
  durationInMinutes?: number; // ISO 8601 duration string
  locale?: string;
  isVisible?: boolean;
}

interface MaintenanceBannerProps {
  tenantId: TenantId;
  defaultConfig: MaintenanceBannerConfig;
  storageKey: string;
  storageStrategy?: 'cookie' | 'localStorage' | 'sessionStorage' | 'memory';
  dismissalDuration?: number;
  onDismiss?: () => void;
  testId: string;
}

/**
 * MaintenanceBanner component for maintenance outages and scheduled maintenance announcements.
 * 
 * This component is specifically designed for maintenance notifications that will
 * eventually use translations. Currently gets text from Growthbook, but structured
 * to support translation keys in the future.
 */
const MaintenanceBanner: React.FC<MaintenanceBannerProps> = ({
  tenantId,
  defaultConfig,
  storageKey,
  storageStrategy = 'cookie',
  dismissalDuration = DISMISSAL_DURATIONS.ONE_DAY,
  testId,
}) => {
  const growthbook = useGrowthBook();

  const growthbookConfig = growthbook.getFeatureValue<MaintenanceBannerConfig>(
    MAINTENANCE_BANNER_FLAG,
    defaultConfig
  );

  // Merge GrowthBook config with defaults to ensure all properties are preserved
  const config = {
    ...defaultConfig,
    ...growthbookConfig,
  };

  const { i18n, t } = useTranslation();

  if (!config?.isVisible || !config.date) {
    return null;
  }

  let message = t(LOCALE_COMMON_PATHS.MAINTENANCE_INTRO_MESSAGE);
  if (config.date) {
    message += ' ' + t(LOCALE_COMMON_PATHS.MAINTENANCE_DATE_MESSAGE, { date: formatI18nDate(new Date(config.date), 'long', i18n.language) });
  }
  if (config.durationInMinutes) {
    message += ' ' + t(LOCALE_COMMON_PATHS.MAINTENANCE_DURATION_MESSAGE, { duration: config.durationInMinutes });
  }
  message += ' ' + t(LOCALE_COMMON_PATHS.MAINTENANCE_END_MESSAGE);

  return (
    <Banner
      variant="floating"
      tenantId={tenantId}
      message={message}
      title={t(LOCALE_COMMON_PATHS.MAINTENANCE_TITLE)}
      severity={'info'}
      showIcon
      icon={<MaintenanceIcon/>}
      buttonText={t(LOCALE_COMMON_PATHS.MAINTENANCE_BUTTON_TEXT)}
      storageKey={storageKey}
      storageStrategy={storageStrategy}
      dismissalDuration={dismissalDuration}
      testId={testId}
    />
  );
};

export default MaintenanceBanner;

